import { useContext, useState, useEffect } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, Controller } from 'react-hook-form';
import { ExpensesSchema, ExpensesType } from '../../types';
import { styles } from '../../../../styles';
import { FinancialStatusContext } from '../../context/financial-status-context';
import Button from '../../../../_sharedComponents/button';
import { formatter } from '../../../../../_utils/format';
import StepHeader from '../../step-header';
import ErrorMessage from '../error-message';
import Label from '../label';

export default function Expenses() {
	const financialContext = useContext(FinancialStatusContext);
	const [totalExpenses, setTotalExpenses] = useState(0);

	const defaultValues: ExpensesType = {
		housing: 0,
		food: 0,
		utilities: 0,
		transportation: 0,
		medical: 0,
		school: 0,
		clothing: 0,
		childSupport: 0,
		alimony: 0,
		other: 0,
		attorneyFees: 0,
		necessities: 0,
		totalExpenses: 0,
	};

	const {
		handleSubmit,
		control,
		watch,
		setValue,
		formState: { errors },
	} = useForm<ExpensesType>({
		mode: 'onSubmit',
		reValidateMode: 'onChange',
		resolver: zodResolver(ExpensesSchema),
		defaultValues,
	});

	const onSubmit = (data: ExpensesType) => {
		financialContext.updateExpenses(data);
		financialContext.updateCurrentStep(8);
		scrollTo({ top: 0, behavior: 'smooth' });
	};

	useEffect(() => {
		// Calculate total expenses
		const expenseFields: (keyof ExpensesType)[] = [
			'housing',
			'food',
			'utilities',
			'transportation',
			'medical',
			'school',
			'clothing',
			'childSupport',
			'alimony',
			'other',
			'attorneyFees',
			'necessities',
		];

		let total = 0;
		expenseFields.forEach((field) => {
			total += watch(field) || 0;
		});
		setTotalExpenses(total);
		setValue('totalExpenses', total);
	}, [
		watch('housing'),
		watch('food'),
		watch('utilities'),
		watch('transportation'),
		watch('medical'),
		watch('school'),
		watch('clothing'),
		watch('childSupport'),
		watch('alimony'),
		watch('other'),
		watch('attorneyFees'),
		watch('necessities'),
	]);

	return (
		<form className="h-full" onSubmit={handleSubmit(onSubmit)}>
			<div className="flex h-full flex-col justify-between">
				<StepHeader step={7} />
				<div>
					<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
						{/* housing */}
						<div className="min-h-24">
							<Label currentStep="expenses" value="housing" />
							<Controller
								control={control}
								name="housing"
								render={({ field }) => (
									<input
										{...field}
										className={`${styles.mediumInput} ${errors.housing ? styles.error : ''}`}
										id="housing"
										onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
										placeholder={financialContext.getPlaceholder({ step: 'expenses', value: 'housing' })}
										type="number"
										value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
									/>
								)}
							/>
							{errors.housing ? (
								<div className="md:max-w-56">
									<ErrorMessage currentStep="expenses" value="housing" />
								</div>
							) : null}
						</div>
						{/* Food */}
						<div className="min-h-24">
							<Label currentStep="expenses" value="food" />
							<Controller
								control={control}
								name="food"
								render={({ field }) => (
									<input
										{...field}
										className={`${styles.mediumInput} ${errors.food ? styles.error : ''}`}
										id="food"
										onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
										placeholder={financialContext.getPlaceholder({ step: 'expenses', value: 'food' })}
										type="number"
										value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
									/>
								)}
							/>
							{errors.food ? (
								<div className="md:max-w-56">
									<ErrorMessage currentStep="expenses" value="food" />
								</div>
							) : null}
						</div>
					</div>
					{/* Utilities and Transportation  */}
					<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
						{/* utilities */}
						<div className="min-h-24">
							<Label currentStep="expenses" value="utilities" />
							<Controller
								control={control}
								name="utilities"
								render={({ field }) => (
									<input
										{...field}
										className={`${styles.mediumInput} ${errors.utilities ? styles.error : ''}`}
										id="utilities"
										onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
										placeholder={financialContext.getPlaceholder({ step: 'expenses', value: 'utilities' })}
										type="number"
										value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
									/>
								)}
							/>
							{errors.utilities ? (
								<div className="md:max-w-56">
									<ErrorMessage currentStep="expenses" value="utilities" />
								</div>
							) : null}
						</div>
						{/* transportation */}
						<div className="min-h-24">
							<Label currentStep="expenses" value="transportation" />
							<Controller
								control={control}
								name="transportation"
								render={({ field }) => (
									<input
										{...field}
										className={`${styles.mediumInput} ${errors.transportation ? styles.error : ''}`}
										id="food"
										onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
										placeholder={financialContext.getPlaceholder({ step: 'expenses', value: 'transportation' })}
										type="number"
										value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
									/>
								)}
							/>
							{errors.transportation ? (
								<div className="md:max-w-56">
									<ErrorMessage currentStep="expenses" value="transportation" />
								</div>
							) : null}
						</div>
					</div>
					{/* Medical and School  */}
					<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
						{/* medical */}
						<div className="min-h-24">
							<Label currentStep="expenses" value="medical" />
							<Controller
								control={control}
								name="medical"
								render={({ field }) => (
									<input
										{...field}
										className={`${styles.mediumInput} ${errors.medical ? styles.error : ''}`}
										id="medical"
										onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
										placeholder={financialContext.getPlaceholder({ step: 'expenses', value: 'medical' })}
										type="number"
										value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
									/>
								)}
							/>
							{errors.medical ? (
								<div className="md:max-w-56">
									<ErrorMessage currentStep="expenses" value="medical" />
								</div>
							) : null}
						</div>
						{/* school */}
						<div className="min-h-24">
							<Label currentStep="expenses" value="school" />
							<Controller
								control={control}
								name="school"
								render={({ field }) => (
									<input
										{...field}
										className={`${styles.mediumInput} ${errors.school ? styles.error : ''}`}
										id="school"
										onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
										placeholder={financialContext.getPlaceholder({ step: 'expenses', value: 'school' })}
										type="number"
										value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
									/>
								)}
							/>
							{errors.school ? (
								<div className="md:max-w-56">
									<ErrorMessage currentStep="expenses" value="school" />
								</div>
							) : null}
						</div>
					</div>
					{/* Clothing and Child Support  */}
					<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
						{/* clothing */}
						<div className="min-h-24">
							<Label currentStep="expenses" value="clothing" />
							<Controller
								control={control}
								name="clothing"
								render={({ field }) => (
									<input
										{...field}
										className={`${styles.mediumInput} ${errors.clothing ? styles.error : ''}`}
										id="clothing"
										onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
										placeholder={financialContext.getPlaceholder({ step: 'expenses', value: 'clothing' })}
										type="number"
										value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
									/>
								)}
							/>
							{errors.clothing ? (
								<div className="md:max-w-56">
									<ErrorMessage currentStep="expenses" value="clothing" />
								</div>
							) : null}
						</div>
						{/* childSupport */}
						<div className="min-h-24">
							<Label currentStep="expenses" value="childSupport" />
							<Controller
								control={control}
								name="childSupport"
								render={({ field }) => (
									<input
										{...field}
										className={`${styles.mediumInput} ${errors.childSupport ? styles.error : ''}`}
										id="childSupport"
										onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
										placeholder={financialContext.getPlaceholder({ step: 'expenses', value: 'childSupport' })}
										type="number"
										value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
									/>
								)}
							/>
							{errors.childSupport ? (
								<div className="md:max-w-56">
									<ErrorMessage currentStep="expenses" value="childSupport" />
								</div>
							) : null}
						</div>
					</div>
					{/* Alimony and Other  */}
					<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
						{/* alimony */}
						<div className="min-h-24">
							<Label currentStep="expenses" value="alimony" />
							<Controller
								control={control}
								name="alimony"
								render={({ field }) => (
									<input
										{...field}
										className={`${styles.mediumInput} ${errors.alimony ? styles.error : ''}`}
										id="alimony"
										onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
										placeholder={financialContext.getPlaceholder({ step: 'expenses', value: 'alimony' })}
										type="number"
										value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
									/>
								)}
							/>
							{errors.alimony ? (
								<div className="md:max-w-56">
									<ErrorMessage currentStep="expenses" value="alimony" />
								</div>
							) : null}
						</div>
						{/* other */}
						<div className="min-h-24">
							<Label currentStep="expenses" value="other" />
							<Controller
								control={control}
								name="other"
								render={({ field }) => (
									<input
										{...field}
										className={`${styles.mediumInput} ${errors.other ? styles.error : ''}`}
										id="other"
										onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
										placeholder={financialContext.getPlaceholder({ step: 'expenses', value: 'other' })}
										type="number"
										value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
									/>
								)}
							/>
							{errors.other ? (
								<div className="md:max-w-56">
									<ErrorMessage currentStep="expenses" value="other" />
								</div>
							) : null}
						</div>
					</div>
					{/* Attorney Fees and Necessities  */}
					<div className="mt-5 flex min-h-24 flex-col space-y-5 md:mt-0 md:space-y-5 lg:flex-row lg:space-x-20 lg:space-y-0">
						{/* attorneyFees */}
						<div className="min-h-24">
							<Label currentStep="expenses" value="attorneyFees" />
							<Controller
								control={control}
								name="attorneyFees"
								render={({ field }) => (
									<input
										{...field}
										className={`${styles.mediumInput} ${errors.attorneyFees ? styles.error : ''}`}
										id="attorneyFees"
										onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
										placeholder={financialContext.getPlaceholder({ step: 'expenses', value: 'attorneyFees' })}
										type="number"
										value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
									/>
								)}
							/>
							{errors.attorneyFees ? (
								<div className="md:max-w-56">
									<ErrorMessage currentStep="expenses" value="attorneyFees" />
								</div>
							) : null}
						</div>
						{/* necessities */}
						<div className="min-h-24">
							<Label currentStep="expenses" value="necessities" />
							<Controller
								control={control}
								name="necessities"
								render={({ field }) => (
									<input
										{...field}
										className={`${styles.mediumInput} ${errors.necessities ? styles.error : ''}`}
										id="necessities"
										onChange={(e) => field.onChange(e.target.value === '' ? 0 : parseInt(e.target.value, 10))} // Handle input changes
										placeholder={financialContext.getPlaceholder({ step: 'expenses', value: 'necessities' })}
										type="number"
										value={field.value === 0 ? '' : field.value} // Show placeholder if value is 0
									/>
								)}
							/>
							{errors.necessities ? (
								<div className="md:max-w-56">
									<ErrorMessage currentStep="expenses" value="necessities" />
								</div>
							) : null}
						</div>
					</div>
				</div>
				<div className="mb-10 flex justify-end space-y-3 md:min-h-24 md:w-3/4">
					<div className="w-full justify-between font-light text-gray-500 lg:flex lg:w-1/2">
						<div>Total estimated expenses:</div>
						<div className="font-semibold">{formatter.format(totalExpenses)}</div>
					</div>
				</div>
				<div className="mx-auto my-10 flex space-x-10">
					<Button
						label={financialContext.language === 'en' ? 'Prev' : 'Anterior'}
						onClick={() => {
							financialContext.updateCurrentStep(6);
							scrollTo({ top: 0, behavior: 'smooth' });
						}}
						type="button"
					/>

					<Button label={financialContext.language === 'en' ? 'Next' : 'Seguiente'} type="submit" />
				</div>
			</div>
		</form>
	);
}
